import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import './Dogs.css';

export default function DogDetail() {
  const { id } = useParams();
  const [dog,        setDog]        = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [activePhoto,setActivePhoto]= useState(null);
  const [form,       setForm]       = useState({ first_name:'', last_name:'', email:'', phone:'', message:'', housing_type:'', has_yard:'', has_other_pets:'', other_pets_desc:'', has_children:'', children_ages:'' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    api.getDog(id)
      .then(d => { setDog(d); setActivePhoto(d.primary_photo); })
      .catch(() => setDog(null))
      .finally(() => setLoading(false));
  }, [id]);

  const allPhotos = () => {
    if (!dog) return [];
    const extras = (() => { try { return JSON.parse(dog.photos || '[]'); } catch { return []; } })();
    return [dog.primary_photo, ...extras].filter(Boolean);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.submitInquiry({ ...form, dog_id: id });
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!dog)    return <div className="container" style={{padding:'120px 24px', textAlign:'center'}}><h2>Dog not found</h2><Link to="/dogs" className="btn btn-primary" style={{marginTop:16}}>Back to Dogs</Link></div>;

  const age = [dog.age_years && `${dog.age_years}y`, dog.age_months && `${dog.age_months}mo`].filter(Boolean).join(' ');

  const traitVal = v => v === 1 ? '✓' : v === 0 ? '✗' : '?';
  const traitCls = v => v === 1 ? '--yes' : v === 0 ? '--no' : '--unk';

  return (
    <div className="dog-detail">
      <div className="dog-detail__hero">
        <div className="container">
          <Link to="/dogs" className="dog-detail__back">← Back to Dogs</Link>
        </div>
      </div>

      <div className="container">
        <div className="dog-detail__grid">
          {/* Photos */}
          <div className="dog-detail__photos">
            <div className="dog-detail__main-photo">
              {activePhoto
                ? <img src={`https://runarounddogtown.com/rescue-api${activePhoto}`} alt={dog.name} />
                : <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'4rem'}}>🐾</div>
              }
            </div>
            {allPhotos().length > 1 && (
              <div className="dog-detail__thumbnails">
                {allPhotos().map((ph, i) => (
                  <div
                    key={i}
                    className={`dog-detail__thumb ${activePhoto === ph ? 'dog-detail__thumb--active' : ''}`}
                    onClick={() => setActivePhoto(ph)}
                  >
                    <img src={`https://runarounddogtown.com/rescue-api${ph}`} alt={`${dog.name} photo ${i+1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="dog-detail__info">
            <h1 className="dog-detail__name">{dog.name}</h1>
            <div className="dog-detail__meta">
              {dog.status && <span className={`badge badge-${dog.status === 'not_available' ? 'pending' : dog.status}`}>{dog.status.replace('_',' ')}</span>}
              {dog.breed  && <span className="dog-detail__meta-item">{dog.breed}</span>}
              {age        && <span className="dog-detail__meta-item">{age}</span>}
              {dog.gender && dog.gender !== 'unknown' && <span className="dog-detail__meta-item">{dog.gender}</span>}
              {dog.size   && <span className="dog-detail__meta-item">{dog.size.replace('_',' ')}</span>}
              {dog.color  && <span className="dog-detail__meta-item">{dog.color}</span>}
            </div>

            {dog.special_needs === 1 && (
              <div className="dog-detail__special">
                💛 <strong>Special Needs:</strong> {dog.special_needs_desc || 'This dog has special needs. Please contact us for more information.'}
              </div>
            )}

            {dog.description && <p className="dog-detail__desc">{dog.description}</p>}

            <div className="dog-detail__traits">
              {[
                { label: 'Good with dogs', val: dog.good_with_dogs },
                { label: 'Good with cats', val: dog.good_with_cats },
                { label: 'Good with kids', val: dog.good_with_kids },
                { label: 'House trained',  val: dog.house_trained  },
              ].map(t => (
                <div className="dog-detail__trait" key={t.label}>
                  <div className={`dog-detail__trait-icon dog-detail__trait-icon${traitCls(t.val)}`}>
                    {t.val === 1 ? '✅' : t.val === 0 ? '❌' : '❓'}
                  </div>
                  {t.label}
                </div>
              ))}
            </div>

            {dog.status === 'available' && (
              <a href="#inquire" className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}>
                Apply to Adopt {dog.name}
              </a>
            )}
          </div>
        </div>

        {/* Inquiry Form */}
        {dog.status === 'available' && (
          <div id="inquire" style={{marginTop:64}}>
            <h2 className="section-title">Adoption Inquiry</h2>
            <p className="section-subtitle">Interested in {dog.name}? Fill out this form and we'll be in touch soon.</p>

            {submitted ? (
              <div className="alert alert-success" style={{maxWidth:600}}>
                🐾 Thank you! We've received your inquiry about {dog.name} and will be in touch soon.
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{maxWidth:700}}>
                {error && <div className="alert alert-error">{error}</div>}

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input className="form-control" required value={form.first_name} onChange={e=>f('first_name',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input className="form-control" required value={form.last_name} onChange={e=>f('last_name',e.target.value)} />
                  </div>
                </div>

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-control" type="email" required value={form.email} onChange={e=>f('email',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" type="tel" value={form.phone} onChange={e=>f('phone',e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Housing Type</label>
                  <select className="form-control" value={form.housing_type} onChange={e=>f('housing_type',e.target.value)}>
                    <option value="">Select...</option>
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16}}>
                  <div className="form-group">
                    <label className="form-label">Have a yard?</label>
                    <select className="form-control" value={form.has_yard} onChange={e=>f('has_yard',e.target.value)}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Other pets?</label>
                    <select className="form-control" value={form.has_other_pets} onChange={e=>f('has_other_pets',e.target.value)}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Children?</label>
                    <select className="form-control" value={form.has_children} onChange={e=>f('has_children',e.target.value)}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>

                {form.has_other_pets === 'true' && (
                  <div className="form-group">
                    <label className="form-label">Describe your other pets</label>
                    <input className="form-control" value={form.other_pets_desc} onChange={e=>f('other_pets_desc',e.target.value)} />
                  </div>
                )}

                {form.has_children === 'true' && (
                  <div className="form-group">
                    <label className="form-label">Ages of children</label>
                    <input className="form-control" placeholder="e.g. 5, 8, 12" value={form.children_ages} onChange={e=>f('children_ages',e.target.value)} />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Tell us about yourself *</label>
                  <textarea className="form-control" required rows={5} placeholder={`Why are you interested in ${dog.name}? Tell us about your home, lifestyle, and experience with dogs.`} value={form.message} onChange={e=>f('message',e.target.value)} />
                </div>

                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : `Submit Inquiry for ${dog.name}`}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
